import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ARCH_SURFACES,
  COMMON_SURFACE_COMBINATIONS,
  QUADRANT_SURFACES,
  SEXTANT_SURFACES,
  SINGLE_SURFACES,
} from "../../utils/surfaceSelectionConstants";
import { toggleSelectionValue } from "../../utils/selectionValueUtils";
import {
  SelectionModalTabs,
  SelectionModalTab,
  SelectionModalSection,
  SelectionSectionHeader,
  SelectionSectionHeaderTitle,
  SelectionModalSectionBody,
  SelectionOptionButton,
  CustomInputRow,
  CustomInput,
  CustomAddButton,
  SurfaceOptionsRow,
  QuadGrid,
  QuadOptionButton,
  QuadOptionCode,
  QuadOptionLabel,
  ArchOptionsRow,
  ArchOptionButton,
} from "../SelectionModal/SelectionModal.styled";

type SurfaceSelectionTab = "surface" | "quad" | "sextant" | "arch";

interface SurfaceSelectionPanelProps {
  value: string[];
  onChange: (value: string[]) => void;
}

const SurfaceSelectionPanel: React.FC<SurfaceSelectionPanelProps> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<SurfaceSelectionTab>("surface");
  const [customValue, setCustomValue] = useState("");

  const handleToggle = (surface: string) => {
    onChange(toggleSelectionValue(value, surface, (item) => item.toUpperCase()));
  };

  const handleCustomAdd = () => {
    if (!customValue.trim()) return;
    onChange(toggleSelectionValue(value, customValue, (item) => item.toUpperCase()));
    setCustomValue("");
  };

  return (
    <>
      <SelectionModalTabs>
        <SelectionModalTab
          type="button"
          active={activeTab === "surface"}
          onClick={() => setActiveTab("surface")}
        >
          {t("treatmentPlan.surfaceTabSurface")}
        </SelectionModalTab>
        <SelectionModalTab
          type="button"
          active={activeTab === "quad"}
          onClick={() => setActiveTab("quad")}
        >
          {t("treatmentPlan.surfaceTabQuad")}
        </SelectionModalTab>
        <SelectionModalTab
          type="button"
          active={activeTab === "sextant"}
          onClick={() => setActiveTab("sextant")}
        >
          {t("treatmentPlan.surfaceTabSextant")}
        </SelectionModalTab>
        <SelectionModalTab
          type="button"
          active={activeTab === "arch"}
          onClick={() => setActiveTab("arch")}
        >
          {t("treatmentPlan.surfaceTabArch")}
        </SelectionModalTab>
      </SelectionModalTabs>

      {activeTab === "surface" && (
        <>
          <SelectionModalSection>
            <SelectionSectionHeader>
              <SelectionSectionHeaderTitle>
                {t("treatmentPlan.singleSurface")}
              </SelectionSectionHeaderTitle>
            </SelectionSectionHeader>
            <SelectionModalSectionBody>
            <SurfaceOptionsRow>
              {SINGLE_SURFACES.map((surface) => (
                <SelectionOptionButton
                  key={surface}
                  type="button"
                  selected={value.includes(surface)}
                  onClick={() => handleToggle(surface)}
                >
                  {surface}
                </SelectionOptionButton>
              ))}
            </SurfaceOptionsRow>
            </SelectionModalSectionBody>
          </SelectionModalSection>

          <SelectionModalSection>
            <SelectionSectionHeader>
              <SelectionSectionHeaderTitle>
                {t("treatmentPlan.commonCombinations")}
              </SelectionSectionHeaderTitle>
            </SelectionSectionHeader>
            <SelectionModalSectionBody>
            <SurfaceOptionsRow>
              {COMMON_SURFACE_COMBINATIONS.map((surface) => (
                <SelectionOptionButton
                  key={surface}
                  type="button"
                  selected={value.includes(surface)}
                  onClick={() => handleToggle(surface)}
                >
                  {surface}
                </SelectionOptionButton>
              ))}
            </SurfaceOptionsRow>
            </SelectionModalSectionBody>
          </SelectionModalSection>

          <SelectionModalSection>
            <SelectionSectionHeader>
              <SelectionSectionHeaderTitle>
                {t("treatmentPlan.customSurface")}
              </SelectionSectionHeaderTitle>
            </SelectionSectionHeader>
            <SelectionModalSectionBody>
            <CustomInputRow>
              <CustomInput
                value={customValue}
                placeholder={t("treatmentPlan.enterPlaceholder")}
                onChange={(event) => setCustomValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleCustomAdd();
                  }
                }}
              />
              <CustomAddButton type="button" onClick={handleCustomAdd}>
                {t("treatmentPlan.add")}
              </CustomAddButton>
            </CustomInputRow>
            </SelectionModalSectionBody>
          </SelectionModalSection>
        </>
      )}

      {activeTab === "quad" && (
        <SelectionModalSectionBody>
          <QuadGrid>
            {QUADRANT_SURFACES.map((quad) => (
              <QuadOptionButton
                key={quad.code}
                type="button"
                selected={value.includes(quad.code)}
                onClick={() => handleToggle(quad.code)}
              >
                <QuadOptionCode>{quad.code}</QuadOptionCode>
                <QuadOptionLabel>{quad.label}</QuadOptionLabel>
              </QuadOptionButton>
            ))}
          </QuadGrid>
        </SelectionModalSectionBody>
      )}

      {activeTab === "sextant" && (
        <SelectionModalSectionBody>
          <SurfaceOptionsRow>
            {SEXTANT_SURFACES.map((sextant) => (
              <SelectionOptionButton
                key={sextant}
                type="button"
                size="md"
                selected={value.includes(sextant)}
                onClick={() => handleToggle(sextant)}
              >
                {sextant}
              </SelectionOptionButton>
            ))}
          </SurfaceOptionsRow>
        </SelectionModalSectionBody>
      )}

      {activeTab === "arch" && (
        <SelectionModalSectionBody>
          <ArchOptionsRow>
            {ARCH_SURFACES.map((arch) => (
              <ArchOptionButton
                key={arch.code}
                type="button"
                selected={value.includes(arch.code)}
                onClick={() => handleToggle(arch.code)}
              >
                <QuadOptionCode>{arch.code}</QuadOptionCode>
                <QuadOptionLabel>{arch.label}</QuadOptionLabel>
              </ArchOptionButton>
            ))}
          </ArchOptionsRow>
        </SelectionModalSectionBody>
      )}
    </>
  );
};

export default SurfaceSelectionPanel;
